import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";
import type { Ref } from "vue";
import { hasProviderForName } from "./iconProviders";
import { cdnUrl, iconsVersion } from "./iconConfig";

/**
 * Shape of the icon manifest JSON fetched from the CDN.
 */
interface IconManifest {
  generatedAt: string;
  iconCount: number;
  icons: Array<{
    name: string;
    file: string;
    aliases: string[];
  }>;
  aliases: Record<string, string>;
  missingAliases: Record<string, string>;
}

/** Normalizes SVG fill/stroke colors to `currentColor`. */
const processSvgContent = (svgContent: string): string =>
  svgContent
    .replaceAll(/fill="[^"]*"/gi, 'fill="currentColor"')
    .replaceAll(/stroke="[^"]*"/gi, 'stroke="currentColor"');

/** Fetch the manifest JSON from the CDN. */
const fetchManifestFromCdn = async (): Promise<IconManifest | null> => {
  const response = await fetch(`${cdnUrl}/core/icons/${iconsVersion}/manifest.json`);
  if (!response.ok) {
    console.error("CDN manifest request failed:", response.status, response.statusText);
    return null;
  }
  return response.json();
};

/** Fetch and process a single SVG from the CDN. */
const fetchAndProcessSvg = async (iconFile: string, iconName: string): Promise<string | null> => {
  try {
    const response = await fetch(`${cdnUrl}/core/icons/${iconsVersion}/${iconFile}`);
    if (!response.ok) {
      console.error("Failed to fetch icon", iconName, response.status, response.statusText);
      return null;
    }
    return processSvgContent(await response.text());
  } catch (error) {
    console.error("Error fetching icon", iconName, error);
    return null;
  }
};

/** Resolve an icon name or alias against the manifest data. */
const resolveIcon = (
  nameOrAlias: string,
  iconsList: IconManifest["icons"],
  aliasMap: IconManifest["aliases"],
  isLoading: boolean,
): string | null => {
  if (!nameOrAlias) return null;
  if (iconsList.some((icon) => icon.name === nameOrAlias)) return nameOrAlias;
  const resolved = aliasMap[nameOrAlias];
  if (resolved) return resolved;
  if (!isLoading && !hasProviderForName(nameOrAlias)) console.warn(`Icon "${nameOrAlias}" not found in manifest`);
  return null;
};

/** Build a manifest snapshot from individual ref values. */
const buildManifest = (
  generatedAt: string | null,
  iconCount: number,
  icons: IconManifest["icons"],
  aliases: IconManifest["aliases"],
  missingAliases: IconManifest["missingAliases"],
): IconManifest => ({
  generatedAt: generatedAt ?? "",
  iconCount,
  icons,
  aliases,
  missingAliases,
});

/** Poll until `isLoading` flips to false, then return the result. */
const pollUntilLoaded = (isLoading: Ref<boolean>, getResult: () => IconManifest | null): Promise<IconManifest | null> =>
  new Promise<IconManifest | null>((resolve) => {
    const id = globalThis.setInterval(() => {
      if (!isLoading.value) {
        clearInterval(id);
        resolve(getResult());
      }
    }, 50);
  });

/** Create a cached, deduplicated SVG fetcher. */
const createSvgFetcher =
  (
    icons: Ref<IconManifest["icons"]>,
    svgCache: Map<string, string>,
    loadingSvgs: Map<string, Promise<string | null>>,
  ) =>
  async (iconName: string): Promise<string | null> => {
    if (svgCache.has(iconName)) return svgCache.get(iconName)!;
    if (loadingSvgs.has(iconName)) return loadingSvgs.get(iconName)!;
    const icon = icons.value.find((i) => i.name === iconName);
    if (!icon) return null;
    const promise = fetchAndProcessSvg(icon.file, iconName)
      .then((svg) => {
        if (svg) svgCache.set(iconName, svg);
        return svg;
      })
      .finally(() => loadingSvgs.delete(iconName));
    loadingSvgs.set(iconName, promise);
    return promise;
  };

/**
 * Icon metadata and SVG fetching store.
 */
export const useIconStore = defineStore("icons", () => {
  // shallowRef: the manifest arrays/objects are replaced wholesale, never mutated in place.
  // Deep reactivity on 400+ icon objects is wasted overhead.
  const icons = shallowRef<IconManifest["icons"]>([]);
  const aliases = shallowRef<IconManifest["aliases"]>({});
  const missingAliases = shallowRef<IconManifest["missingAliases"]>({});
  const iconCount = ref<number>(0);
  const generatedAt = ref<string | null>(null);
  const isLoadingList = ref(false);
  // Plain Maps — not reactive. Cache reads/writes must not trigger Vue tracking or re-renders.
  const svgCache = new Map<string, string>();
  const loadingSvgs = new Map<string, Promise<string | null>>();

  const currentManifest = () =>
    buildManifest(generatedAt.value, iconCount.value, icons.value, aliases.value, missingAliases.value);

  const fetchIconList = async (): Promise<IconManifest | null> => {
    if (icons.value.length > 0) return currentManifest();
    if (isLoadingList.value)
      return pollUntilLoaded(isLoadingList, () => (icons.value.length > 0 ? currentManifest() : null));
    isLoadingList.value = true;
    try {
      const data = await fetchManifestFromCdn();
      if (!data) return null;
      icons.value = data.icons;
      aliases.value = data.aliases;
      missingAliases.value = data.missingAliases;
      iconCount.value = data.iconCount;
      generatedAt.value = data.generatedAt;
      return data;
    } catch (error) {
      console.error("Error fetching icons from CDN:", error);
      return null;
    } finally {
      isLoadingList.value = false;
    }
  };

  const fetchSvgContent = createSvgFetcher(icons, svgCache, loadingSvgs);
  const clearCache = (): void => {
    svgCache.clear();
    loadingSvgs.clear();
  };
  return {
    icons,
    aliases,
    missingAliases,
    iconCount,
    generatedAt,
    isLoadingList,
    fetchIconList,
    resolveIconName: (nameOrAlias: string) => resolveIcon(nameOrAlias, icons.value, aliases.value, isLoadingList.value),
    fetchSvgContent,
    clearCache,
  };
});

/**
 * Strongly-typed icon store instance.
 *
 * Usage:
 * ```ts
 * const store: IconStore = useIconStore();
 * ```
 */
export type IconStore = ReturnType<typeof useIconStore>;
